import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { Loader2, Key, Copy, Check } from "lucide-react";
import { getCurrentUser, getCurrentShop } from "@/lib/auth";

export function AuthorizationCodeGenerator() {
  const [code, setCode] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [expiresIn, setExpiresIn] = useState(30);
  const [copied, setCopied] = useState(false);

  const generateCode = async () => {
    const user = getCurrentUser();
    const shop = getCurrentShop();

    if (!user) {
      toast({
        title: "Error",
        description: "No se pudo identificar al supervisor",
        variant: "destructive",
      });
      return;
    }

    if (!shop) {
      toast({
        title: "Error",
        description: "No se pudo identificar la tienda",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch("/api/shifts/authorization-code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-shop-id": shop.id,
        },
        credentials: "include",
        body: JSON.stringify({
          userId: user.id,
          description: "Código generado desde el panel",
          expiresInMinutes: expiresIn,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setCode(data.code);
        toast({
          title: "✅ Código generado",
          description: `Código: ${data.code} - Válido por ${data.expiresInMinutes} minutos`,
        });
      } else {
        throw new Error(data.error || "Error al generar código");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo generar el código",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = () => {
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      toast({
        title: "📋 Copiado",
        description: "Código copiado al portapapeles",
        duration: 2000,
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5" />
          Generar Código de Supervisor
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Genera un código de autorización para que los vendedores puedan forzar
          el cierre de turno cuando haya una diferencia significativa.
        </p>

        <div>
          <label className="text-sm font-medium mb-1 block">
            Validez del código (minutos)
          </label>
          <Input
            type="number"
            min="5"
            max="120"
            value={expiresIn}
            onChange={(e) => setExpiresIn(parseInt(e.target.value) || 30)}
            className="w-32"
          />
          <p className="text-xs text-muted-foreground mt-1">
            El código expirará después de este tiempo (5-120 minutos)
          </p>
        </div>

        <Button
          onClick={generateCode}
          disabled={isGenerating}
          className="w-full"
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generando...
            </>
          ) : (
            <>
              <Key className="mr-2 h-4 w-4" />
              Generar Nuevo Código
            </>
          )}
        </Button>

        {code && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-700 mb-1 text-center">
              Código generado:
            </p>
            <div className="flex items-center justify-center gap-2">
              <p className="text-3xl font-mono font-bold text-green-800 tracking-wider">
                {code}
              </p>
              <Button
                variant="ghost"
                size="icon"
                onClick={copyToClipboard}
                className="h-8 w-8"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-green-600 mt-2 text-center">
              Válido por {expiresIn} minutos. Entrégaselo al vendedor.
            </p>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
          <p className="text-xs text-blue-800">
            ℹ️ El vendedor debe ingresar este código en el modal de cierre de
            turno cuando la diferencia exceda el umbral permitido.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
